import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { Settings, Route, Play, AlertCircle, CheckCircle, Search, RefreshCw, Pause, ExternalLink, Shield } from 'lucide-react';
import { getApiBaseUrl } from '../../config/apiBase';

const API_BASE_URL = getApiBaseUrl();

const SMPPRoutingEngine = () => {
  const navigate = useNavigate();
  const [routingStatus, setRoutingStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModeModal, setShowModeModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [mobileNumber, setMobileNumber] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [formData, setFormData] = useState({
    routing_mode: 'PRIORITY',
    failover_enabled: true
  });
  const intervalRef = useRef(null);

  useEffect(() => {
    fetchRoutingStatus();
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchRoutingStatus, 10000);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [autoRefresh]);

  const fetchRoutingStatus = async () => {
    try {
      const response = await api.get('/super/smpp/routing/status');
      setRoutingStatus(response.data);
      setFormData({
        routing_mode: response.data.routing_mode,
        failover_enabled: response.data.failover_enabled
      });
      setLastUpdated(new Date());
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch routing status:', error);
      setLoading(false);
    }
  };

  const handleUpdateMode = async () => {
    try {
      await api.post('/super/smpp/routing/update-mode', formData
      );
      alert('Routing mode updated successfully');
      setShowModeModal(false);
      fetchRoutingStatus();
    } catch (error) {
      alert('Failed to update routing mode: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleTestRoute = async () => {
    if (!mobileNumber) {
      alert('Please enter a mobile number');
      return;
    }

    try {
      const response = await api.post('/super/smpp/routing/test',
        { mobile_number: mobileNumber }
      );
      setTestResult(response.data);
    } catch (error) {
      alert('Failed to test route: ' + (error.response?.data?.error || error.message));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
              <Route className="w-5 h-5 text-white" />
            </div>
            Routing Engine Control
          </h1>
          <p className="text-sm text-gray-500 mt-1">Configure routing mode and test route selection</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg">
            {autoRefresh && <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />}
            <span className="text-xs text-gray-500">{autoRefresh ? 'Live' : 'Paused'}</span>
            {lastUpdated && <span className="text-xs text-gray-400">| {lastUpdated.toLocaleTimeString()}</span>}
          </div>
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`p-2 rounded-lg border transition-colors ${autoRefresh ? 'bg-green-50 text-green-600 border-green-200' : 'bg-gray-50 text-gray-500 border-gray-200'}`}
            title={autoRefresh ? 'Pause auto-refresh' : 'Resume auto-refresh'}
          >
            {autoRefresh ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>
          <button
            onClick={fetchRoutingStatus}
            className="p-2 rounded-lg bg-indigo-50 text-indigo-600 border border-indigo-200 hover:bg-indigo-100 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => navigate('/admin/routing-policies')}
            className="flex items-center gap-1.5 px-3 py-2 bg-purple-50 text-purple-700 border border-purple-200 rounded-lg hover:bg-purple-100 text-xs font-medium transition-colors"
          >
            <Shield className="w-3.5 h-3.5" />
            Routing Policies
            <ExternalLink className="w-3 h-3" />
          </button>
        </div>
      </div>

      {routingStatus && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Routing Mode</h3>
                <Settings className="w-6 h-6 text-blue-500" />
              </div>
              <p className="text-3xl font-bold text-gray-900 mb-2">
                {routingStatus.routing_mode}
              </p>
              <p className="text-sm text-gray-600">Current routing strategy</p>
            </div>

            <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Failover</h3>
                {routingStatus.failover_enabled ? (
                  <CheckCircle className="w-6 h-6 text-green-500" />
                ) : (
                  <AlertCircle className="w-6 h-6 text-red-500" />
                )}
              </div>
              <p className="text-3xl font-bold text-gray-900 mb-2">
                {routingStatus.failover_enabled ? 'Enabled' : 'Disabled'}
              </p>
              <p className="text-sm text-gray-600">Automatic failover status</p>
            </div>

            <div className="bg-white rounded-lg shadow p-6 border-l-4 border-purple-500">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Active Rules</h3>
                <Route className="w-6 h-6 text-purple-500" />
              </div>
              <p className="text-3xl font-bold text-gray-900 mb-2">
                {routingStatus.active_rules}
              </p>
              <p className="text-sm text-gray-600">Configured routing rules</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Routing Configuration</h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">Available Routing Modes</h4>
                    <ul className="space-y-2 text-sm text-gray-700">
                      <li className="flex items-start gap-2">
                        <span className="font-semibold text-blue-600 min-w-24">PRIORITY:</span>
                        <span>Routes based on priority value (highest first)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="font-semibold text-green-600 min-w-24">COST:</span>
                        <span>Routes based on lowest cost per message</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="font-semibold text-purple-600 min-w-24">FAILOVER:</span>
                        <span>Primary route with automatic backup failover</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="font-semibold text-orange-600 min-w-24">LOAD:</span>
                        <span>Load balancing across multiple routes</span>
                      </li>
                    </ul>
                  </div>

                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">Current Settings</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Mode:</span>
                        <span className="text-sm font-medium text-gray-900">
                          {routingStatus.routing_mode}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Failover:</span>
                        <span className={`text-sm font-medium ${routingStatus.failover_enabled ? 'text-green-600' : 'text-red-600'}`}>
                          {routingStatus.failover_enabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Last Updated:</span>
                        <span className="text-sm font-medium text-gray-900">
                          {new Date(routingStatus.updated_at).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => setShowModeModal(true)}
                    className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
                  >
                    <Settings className="w-5 h-5" />
                    Update Routing Mode
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Test Route Selection</h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mobile Number
                    </label>
                    <input
                      type="text"
                      value={mobileNumber}
                      onChange={(e) => setMobileNumber(e.target.value)}
                      placeholder="Enter mobile number (e.g., 919876543210)"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  <button
                    onClick={handleTestRoute}
                    className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
                  >
                    <Search className="w-5 h-5" />
                    Test Route
                  </button>

                  {testResult && (
                    <div className={`border rounded-lg p-4 ${testResult.matched ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
                      <div className="flex items-center gap-2 mb-3">
                        {testResult.matched ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-red-600" />
                        )}
                        <h4 className={`font-semibold ${testResult.matched ? 'text-green-900' : 'text-red-900'}`}>
                          {testResult.matched ? 'Route Found' : 'No Route Found'}
                        </h4>
                      </div>

                      {testResult.matched && testResult.route && (
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Route Name:</span>
                            <span className="font-medium text-gray-900">{testResult.route.route_name}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Connector:</span>
                            <span className="font-medium text-gray-900">{testResult.route.connector_name}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Operator:</span>
                            <span className="font-medium text-gray-900">{testResult.route.operator_name}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Priority:</span>
                            <span className="font-medium text-gray-900">{testResult.route.priority}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Route Type:</span>
                            <span className="font-medium text-gray-900">{testResult.route.route_type}</span>
                          </div>
                        </div>
                      )}

                      {!testResult.matched && (
                        <p className="text-sm text-red-800">
                          {testResult.message}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-semibold text-yellow-900 mb-1">
                  Routing Engine Guidelines
                </h4>
                <ul className="text-sm text-yellow-800 space-y-1">
                  <li>• Routing mode changes take effect immediately for new messages</li>
                  <li>• Test route function performs a dry-run without sending actual messages</li>
                  <li>• Failover automatically switches to backup routes when primary fails</li>
                  <li>• Configure routes in the "Outbound Routes" section before testing</li>
                </ul>
              </div>
            </div>
          </div>
        </>
      )}

      {showModeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Update Routing Mode</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Routing Mode
                </label>
                <select
                  value={formData.routing_mode}
                  onChange={(e) => setFormData({...formData, routing_mode: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="PRIORITY">Priority-Based</option>
                  <option value="COST">Cost-Based</option>
                  <option value="FAILOVER">Failover</option>
                  <option value="LOAD">Load Balancing</option>
                </select>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.failover_enabled}
                  onChange={(e) => setFormData({...formData, failover_enabled: e.target.checked})}
                  className="mr-2"
                />
                <label className="text-sm text-gray-700">
                  Enable Automatic Failover
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowModeModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateMode}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Update Mode
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SMPPRoutingEngine;
